import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { api } from '../App';
import { 
  BookOpen, Brain, Sparkles, CheckCircle2, ArrowRight, Clock, 
  Shield, Zap, Gift, User, Mail, Lock, Eye, EyeOff
} from 'lucide-react';

export default function FreeTestPage({ setUser }) {
  const navigate = useNavigate();
  const [step, setStep] = useState('intro'); // intro, register, login
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  const benefits = [
    { icon: <Brain className="w-5 h-5" />, text: "Bilimsel 9 Mizaç Tipi Analizi" },
    { icon: <Sparkles className="w-5 h-5" />, text: "GPT-5.2 ile Detaylı AI Raporu" },
    { icon: <Clock className="w-5 h-5" />, text: "Sadece 10-15 Dakika" },
    { icon: <Gift className="w-5 h-5" />, text: "Tamamen Ücretsiz" },
    { icon: <Shield className="w-5 h-5" />, text: "Gizlilik Garantisi" }
  ];

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Şifreler eşleşmiyor');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('Şifre en az 6 karakter olmalı');
      return;
    }

    setLoading(true);
    try {
      // Register as individual user
      const registerResponse = await api.post('/auth/register', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: 'individual'
      });

      // Auto login after registration
      const loginResponse = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });

      const { access_token, user } = loginResponse.data;
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      
      toast.success('Kayıt başarılı! Envantere yönlendiriliyorsunuz...');
      navigate('/assessment');
    } catch (error) {
      console.error('Registration failed:', error);
      toast.error(error.response?.data?.detail || 'Kayıt başarısız');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await api.post('/auth/login', {
        email: formData.email,
        password: formData.password
      });

      const { access_token, user } = response.data;
      localStorage.setItem('token', access_token);
      api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      setUser(user);
      
      toast.success('Giriş başarılı!');
      navigate('/assessment');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Giriş başarısız');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-600 to-teal-600 rounded-xl flex items-center justify-center">
              <BookOpen className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-xl font-bold text-slate-800">Mizaç360</h1>
          </div>
          <Badge className="bg-gradient-to-r from-emerald-100 to-teal-100 text-emerald-700 border-emerald-200 px-4 py-2">
            <Gift className="w-4 h-4 mr-2" />
            Ücretsiz Mizaç Testi
          </Badge>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {step === 'intro' && (
          <div className="text-center">
            {/* Hero */}
            <div className="mb-12">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-600 text-white px-6 py-3 rounded-full mb-6 shadow-lg">
                <Zap className="w-5 h-5" />
                <span className="font-semibold">%100 Ücretsiz</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                <span className="bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">
                  Mizaç Tipinizi
                </span>
                <br />
                <span className="text-slate-800">Şimdi Keşfedin!</span>
              </h1>
              
              <p className="text-xl text-slate-600 max-w-2xl mx-auto mb-8">
                GPT-5.2 yapay zeka ile hazırlanmış kişiselleştirilmiş mizaç raporunuzu 
                tamamen ücretsiz alın. Kendinizi daha iyi tanıyın!
              </p>
            </div>

            {/* Benefits */}
            <div className="grid md:grid-cols-5 gap-4 max-w-4xl mx-auto mb-12">
              {benefits.map((benefit, index) => (
                <Card key={index} className="border-none shadow-lg bg-white/80 backdrop-blur">
                  <CardContent className="p-4 text-center">
                    <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-3 text-emerald-600">
                      {benefit.icon}
                    </div>
                    <p className="text-sm font-medium text-slate-700">{benefit.text}</p>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-lg px-10 py-7 rounded-2xl shadow-xl"
                onClick={() => setStep('register')}
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Ücretsiz Teste Başla
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                className="text-lg px-8 py-7 rounded-2xl"
                onClick={() => setStep('login')}
              >
                Zaten hesabım var
              </Button>
            </div>

            {/* Trust badges */}
            <div className="mt-12 flex items-center justify-center gap-6 text-slate-500">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5" />
                <span className="text-sm">SSL Güvenlik</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-sm">KVKK Uyumlu</span>
              </div>
              <div className="flex items-center gap-2">
                <Brain className="w-5 h-5" />
                <span className="text-sm">Bilimsel Yöntem</span>
              </div>
            </div>
          </div>
        )}

        {step === 'register' && (
          <div className="max-w-md mx-auto">
            <Card className="shadow-2xl border-none">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <User className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl">Ücretsiz Kayıt Ol</CardTitle>
                <CardDescription>
                  Hemen kayıt ol ve mizaç testine başla
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div>
                    <Label>Ad Soyad</Label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        placeholder="Adınız Soyadınız"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="ornek@email.com"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Şifre</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="••••••••"
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <Label>Şifre Tekrar</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                        placeholder="••••••••"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 py-6"
                    disabled={loading}
                  >
                    {loading ? 'Kaydediliyor...' : 'Kayıt Ol ve Teste Başla'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
                
                <div className="mt-6 text-center">
                  <button 
                    onClick={() => setStep('login')}
                    className="text-sm text-slate-600 hover:text-emerald-600"
                  >
                    Zaten hesabın var mı? <span className="font-semibold">Giriş Yap</span>
                  </button>
                </div>
                
                <div className="mt-4 text-center">
                  <button 
                    onClick={() => setStep('intro')}
                    className="text-sm text-slate-400 hover:text-slate-600"
                  >
                    ← Geri Dön
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {step === 'login' && (
          <div className="max-w-md mx-auto">
            <Card className="shadow-2xl border-none">
              <CardHeader className="text-center pb-2">
                <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl">Giriş Yap</CardTitle>
                <CardDescription>
                  Hesabınla giriş yap ve teste devam et
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <Label>Email</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        placeholder="ornek@email.com"
                        className="pl-10"
                        required
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Şifre</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                      <Input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="••••••••"
                        className="pl-10 pr-10"
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 py-6"
                    disabled={loading}
                  >
                    {loading ? 'Giriş yapılıyor...' : 'Giriş Yap'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </form>
                
                <div className="mt-6 text-center">
                  <button 
                    onClick={() => setStep('register')}
                    className="text-sm text-slate-600 hover:text-emerald-600"
                  >
                    Hesabın yok mu? <span className="font-semibold">Kayıt Ol</span>
                  </button>
                </div>
                
                <div className="mt-4 text-center">
                  <button 
                    onClick={() => setStep('intro')}
                    className="text-sm text-slate-400 hover:text-slate-600"
                  >
                    ← Geri Dön
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t py-8 mt-12">
        <div className="max-w-6xl mx-auto px-6 text-center text-slate-500 text-sm">
          <p>© 2025 Mizaç360. Tüm hakları saklıdır.</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="/privacy" className="hover:text-emerald-600">Gizlilik Politikası</a>
            <a href="/terms" className="hover:text-emerald-600">Kullanım Koşulları</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
